import { Box, Flex } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import FullScreenLoader from 'components/FullScreenLoader';
import { getBlocks } from 'redux/ModelsSlice';
import {
  setIsPageBarOpen,
  createDashboardID,
  fetchDashboardID,
  FetchModelBlocks,
  getDashboardBlockStatus,
  getEditInstrumentData,
  getFetchDashboardStatus,
  getSelectedDashboardScenario,
  getCompareDashboardScenario,
  resetDashboardSliceV2,
  selectDashboardScenario,
  selectCompareDashboardScenario,
  fetchDashboardMetadata,
  getAllInstruments,
  createInstrument,
  updateInstrument,
  setAllInstruments,
  fetchAllDataValues,
  getAllInstrumentOutputs,
  getEditInstrumentOutput,
  setAllInstrumentOutputs,
  getDashboard,
  handleEditCancel,
  getAllInstrumentOutputsStatus,
  setSelectedPage,
  getSelectedPage,
  getIsPageBarOpen,
  getTimeFilter,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';
import { STATUS } from 'projectConstants';
import { getAllScenarios } from 'redux/ScenarioSlice';

import useInstrumentDates from './hooks/useInstrumentDates';
import EmptyDashboard from './EmptyDashboard';
import Header from './header';
import Grid from './Grid';
import DashboardModal from './DashboardModal';
import ErrorFull from './ErrorFull';
import PageBar from './PageBar';
import PageModal from './PageModal';
import PageInfo from './PageInfo';

interface Properties {
  flags?: any;
}

const Dashboard = ({ flags }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const allInstruments: Array<any> = useSelector(getAllInstruments);
  const allInstrumentsOutputs: Array<any> = useSelector(getAllInstrumentOutputs);
  const allInstrumentOutputsStatus = useSelector(getAllInstrumentOutputsStatus);
  const editInstrumentOutput = useSelector(getEditInstrumentOutput);
  const timeFilter = useSelector(getTimeFilter);
  const model = useSelector(getBlocks);
  const dashboardBlockStatus = useSelector(getDashboardBlockStatus);
  const dashboardStatus = useSelector(getFetchDashboardStatus);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const compareDashboardScenario = useSelector(getCompareDashboardScenario);
  const widget = useSelector(getEditInstrumentData);
  const scenarios = useSelector(getAllScenarios);
  const editData = useSelector(getEditInstrumentData);
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  const isPageBarOpen = useSelector(getIsPageBarOpen);
  const { globalReportingPeriod } = useInstrumentDates();

  useEffect(() => {
    return () => {
      dispatch(resetDashboardSliceV2());
    };
  }, []);

  useEffect(() => {
    if (model) {
      dispatch(FetchModelBlocks({ modelId: Number(model.id) }));
    }
  }, [model]);

  useEffect(() => {
    if (!selectedDashboardScenario && scenarios && scenarios?.length && model) {
      // Gloabl Plan Page Group By Filter sync
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      if (modalFilters?.scenario) {
        const scenario = scenarios.find((item: any) => item.id === modalFilters?.scenario?.id);
        if (scenario) {
          dispatch(selectDashboardScenario(scenario));
        } else {
          const baseScenario = scenarios.find((item: any) => item.is_base);
          if (baseScenario) {
            dispatch(selectDashboardScenario(baseScenario));
          }
        }
      } else {
        const baseScenario = scenarios.find((item: any) => item.is_base);
        if (baseScenario) {
          dispatch(selectDashboardScenario(baseScenario));
        }
      }
    }
  }, [scenarios, model]);

  useEffect(() => {
    if (scenarios && scenarios?.length && model) {
      // Load compare scenario from localStorage
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[model.id] || {};
      if (modalFilters?.compareScenario) {
        const scenario = scenarios.find(
          (item: any) => item.id === modalFilters?.compareScenario?.id,
        );
        // Only load if scenario exists, is different from selected scenario, and not already set
        if (
          scenario &&
          scenario.id !== selectedDashboardScenario?.id &&
          compareDashboardScenario?.id !== scenario.id
        ) {
          dispatch(selectCompareDashboardScenario(scenario));
        }
      } else if (compareDashboardScenario && !modalFilters?.compareScenario) {
        // Clear compare scenario if it's not in localStorage
        dispatch(selectCompareDashboardScenario(null));
      }
    }
  }, [scenarios, model, compareDashboardScenario, selectedDashboardScenario]);

  const isLoading = useMemo(
    () =>
      !model ||
      !selectedDashboardScenario ||
      dashboardBlockStatus !== STATUS.SUCCESS ||
      dashboardStatus !== STATUS.SUCCESS ||
      !dashboard?.id,
    [model, dashboardBlockStatus, dashboardStatus, selectedDashboardScenario, dashboard],
  );

  const handleCancel = () => {
    dispatch(handleEditCancel());
  };

  const handleCreate = () => {
    const compareScenarioId = compareDashboardScenario?.id || null;

    if (editData.id) {
      dispatch(
        updateInstrument({
          request_body: {
            ...editData,
            save: true,
            data_values: false,
            scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
            reporting_period: globalReportingPeriod,
            compare_scenario: compareScenarioId,
          },
        }),
      ).then((result) => {
        const foundIndex = allInstruments.findIndex(
          (instrument: any) => instrument.id === editData.id,
        );
        if (foundIndex > -1) {
          const list = JSON.parse(JSON.stringify(allInstruments));
          list[foundIndex] = result.payload;
          dispatch(setAllInstruments([...list]));
          dispatch(
            setAllInstrumentOutputs({
              ...allInstrumentsOutputs,
              [result.payload.id]: { ...editInstrumentOutput },
            }),
          );
        }
        handleCancel();
      });
    } else {
      dispatch(
        createInstrument({
          request_body: {
            ...editData,
            save: true,
            data_values: false,
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
            scenario_id: selectedDashboardScenario?.id ? `${selectedDashboardScenario?.id}` : '',
            reporting_period: globalReportingPeriod,
            compare_scenario: compareScenarioId,
          },
        }),
      ).then((result: any) => {
        dispatch(setAllInstruments([...allInstruments, { ...result.payload }]));
        dispatch(
          setAllInstrumentOutputs({
            ...allInstrumentsOutputs,
            [result.payload.id]: { ...editInstrumentOutput, id: result.payload.id },
          }),
        );
        handleCancel();
      });
    }
    dispatch(setAllInstruments([...allInstruments]));
  };

  const dashboardPageMetadata = (dashboardId: string) => {
    dispatch(fetchDashboardMetadata({ dashboardId }));
  };

  const fetchPageData = (pageId: any, scenarioId: any) => {
    dashboardPageMetadata(pageId);
    if (timeFilter?.start && timeFilter?.end) {
      dispatch(
        fetchAllDataValues({
          pageId,
          scenarioId,
          start_date: timeFilter?.start || '',
          end_date: timeFilter?.end || '',
        }),
      );
    }
  };

  useEffect(() => {
    if (model?.id && !dashboard?.id) {
      const yr = new Date(model?.time_properties?.starting_period).getUTCFullYear().toString();
      const fiscalStart = `${model?.time_properties?.fy_start_month}-${yr[2]}${yr[3]}`;
      dispatch(fetchDashboardID({ model_id: model.id }))
        .then((response) => {
          if (!response.payload?.id) {
            dispatch(
              createDashboardID({
                request_body: {
                  name: 'Dashboard',
                  model_id: Number(model.id),
                  start_time:
                    model?.time_properties?.time_granularity === 'M'
                      ? fiscalStart
                      : `Jan-${yr[2]}${yr[3]}`,
                  description: '',
                },
              }),
            );
          }
        })
        .catch(() => {
          dispatch(
            createDashboardID({
              request_body: {
                name: 'Dashboard',
                model_id: Number(model.id),
                start_time:
                  model?.time_properties?.time_granularity === 'M'
                    ? fiscalStart
                    : `Jan-${yr[2]}${yr[3]}`,
                description: '',
              },
            }),
          );
        });
    }
  }, [model?.id, dashboard]);

  const setSelectedPageAndPersist = (page: any) => {
    dispatch(setSelectedPage(page));
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[model.id] = {
      ...data[model.id],
      selectedPageId: page?.id,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  };

  useEffect(() => {
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[model?.id] || {};
    if (typeof modalFilters.isPageBarOpen === 'boolean') {
      dispatch(setIsPageBarOpen(modalFilters.isPageBarOpen));
    }
  }, [model?.id, dispatch]);

  useEffect(() => {
    if (dashboard?.pages?.length) {
      if (!selectedPage) {
        const pageWithMinPosition = dashboard.pages.reduce((minPage: any, page: any) =>
          page.position < minPage.position ? page : minPage,
        );
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        const modalFilters = data[model.id] || {};
        if (modalFilters?.selectedPageId) {
          const page = dashboard.pages.find((p: any) => p.id === modalFilters.selectedPageId);
          if (page) {
            setSelectedPageAndPersist(page);
          }
        } else {
          setSelectedPageAndPersist(pageWithMinPosition || dashboard?.pages[0]);
        }
      } else {
        const currentPage = dashboard?.pages.find((page: any) => page.id === selectedPage?.id);
        const pageWithMinPosition = dashboard?.pages.reduce((minPage: any, page: any) =>
          page.position < minPage.position ? page : minPage,
        );
        setSelectedPageAndPersist(currentPage || pageWithMinPosition || dashboard?.pages[0]);
      }
    }
  }, [dashboard?.pages]);

  useEffect(() => {
    if (selectedPage && selectedDashboardScenario) {
      fetchPageData(selectedPage.id, selectedDashboardScenario?.id);
    }
  }, [selectedPage?.id, selectedDashboardScenario?.id, timeFilter]);

  const refetchAllInstruments = () => {
    if (selectedPage?.id && selectedDashboardScenario?.id) {
      dashboardPageMetadata(selectedPage?.id);
      if (timeFilter?.start && timeFilter?.end) {
        dispatch(
          fetchAllDataValues({
            pageId: selectedPage?.id,
            scenarioId: selectedDashboardScenario?.id,
            start_date: timeFilter?.start || '',
            end_date: timeFilter?.end || '',
          }),
        );
      }
    }
  };

  if (isLoading) {
    return (
      <Box height='100%' width='100%' p={1}>
        <FullScreenLoader
          style={{ position: 'absolute' }}
          width='calc(100vw - 4.5rem)'
          height={`calc(100vh - ${flags?.uiRevamp ? '215px' : '300px'})`}
        />
      </Box>
    );
  }

  return (
    // Flex column so the dashboard header takes its natural height and the
    // content area fills all remaining space — no magic-number calc() needed.
    // px={8} (2rem) provides the horizontal inset that the dashboard Header's
    // mx={'-2rem'} trick relies on to extend its divider line to full width.
    <Box
      // height='100%'
      display='flex'
      flexDirection='column'
      px={8}
      mx={!flags?.uiRevamp ? '-8' : '0'}
      pt={flags?.uiRevamp ? '4' : '0'}
      height={`calc(100vh - ${!flags?.uiRevamp ? '215px' : '70px'})`}
    >
      <Header />
      {/* mx={-8} cancels the outer px so PageBar can reach the left edge */}
      <Flex flex='1' minH='0' mx={-8} overflow='hidden'>
        {isPageBarOpen && <PageBar />}
        <Box flex='1' minW='0' overflowY='auto' className='scroll' px={5} pt={2} pb={8}>
          {allInstrumentOutputsStatus === STATUS.FAILED ? (
            <ErrorFull onRetry={refetchAllInstruments} />
          ) : (
            <>
              {selectedPage && <PageInfo />}

              {allInstruments?.length ? (
                <Grid allInstruments={allInstruments} />
              ) : (
                <EmptyDashboard />
              )}
            </>
          )}
        </Box>
      </Flex>

      {widget?.instrument_type && (
        <DashboardModal handleCancel={handleCancel} handleCreate={handleCreate} />
      )}

      <PageModal />
    </Box>
  );
};

export default withLDConsumer()(Dashboard);
